import { createContext, useContext, useEffect, useState } from "react"
import { tenantConfig } from "../lib/tenant"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "dark",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )

  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove("light", "dark")

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"

      root.classList.add(systemTheme)
    } else {
      root.classList.add(theme)
    }

    // Aplicar configurações salvas de tema
    applySavedThemeSettings()
  }, [theme])

  const applySavedThemeSettings = () => {
    // Don't override non-custom presets
    const activePreset = document.documentElement.getAttribute("data-preset") || "custom"
    if (activePreset !== "custom") return

    // Whitelabel tenants define their own CSS variables — don't override with inline styles
    if (document.documentElement.hasAttribute("data-tenant")) return

    // Aplicar tema de cor (padrão: colorTheme do tenant ou 'blue')
    const colorTheme = localStorage.getItem("theme-color") || tenantConfig.colorTheme || "blue"
    applyColorTheme(colorTheme)

    // Aplicar tema de fundo (padrão: neutral)
    const backgroundTheme = localStorage.getItem("theme-background") || "neutral"
    applyBackgroundTheme(backgroundTheme)
  }

  const applyColorTheme = (colorTheme: string) => {
    const root = document.documentElement
    const isDark = root.classList.contains('dark')
    
    // Remover classes de tema anteriores
    const themes = ["blue", "green", "neutral", "orange", "red", "rose", "violet", "yellow"]
    themes.forEach(t => root.classList.remove(`theme-${t}`))
    
    // Adicionar nova classe de tema
    root.classList.add(`theme-${colorTheme}`)
    
    // Aplicar cores baseadas no tema selecionado (simplificado)
    const colorMap: Record<string, { primary: string, ring: string }> = {
      blue: { primary: isDark ? "217.2 91.2% 59.8%" : "221.2 83.2% 53.3%", ring: isDark ? "224.3 76.3% 48%" : "221.2 83.2% 53.3%" },
      green: { primary: isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%", ring: isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%" },
      neutral: { primary: isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%", ring: isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%" },
      orange: { primary: "27.8 87.2% 57.8%", ring: "27.8 87.2% 57.8%" },
      red: { primary: "0 84.2% 60.2%", ring: "0 84.2% 60.2%" },
      rose: { primary: "346.8 77.2% 49.8%", ring: "346.8 77.2% 49.8%" },
      violet: { primary: "262.1 83.3% 57.8%", ring: "262.1 83.3% 57.8%" },
      yellow: { primary: "47.9 95.8% 53.1%", ring: "47.9 95.8% 53.1%" }
    }

    const colors = colorMap[colorTheme]
    if (colors) {
      root.style.setProperty("--primary", colors.primary)
      root.style.setProperty("--ring", colors.ring)
      root.style.setProperty("--chart-1", colors.primary)
      root.style.setProperty("--sidebar-primary", colors.primary)
      root.style.setProperty("--sidebar-ring", colors.ring)
    }
  }

  const applyBackgroundTheme = (background: string) => {
    const root = document.documentElement
    const isDark = root.classList.contains('dark')
    
    // Configurações básicas de background
    const bgMap: Record<string, { background: string, card: string, muted: string, border: string }> = {
      default: {
        background: isDark ? "222.2 84% 4.9%" : "0 0% 100%",
        card: isDark ? "222.2 84% 4.9%" : "0 0% 100%",
        muted: isDark ? "217.2 32.6% 17.5%" : "210 40% 96.1%",
        border: isDark ? "217.2 32.6% 17.5%" : "214.3 31.8% 91.4%"
      },
      slate: {
        background: isDark ? "222.2 47% 11%" : "0 0% 100%",
        card: isDark ? "217.2 33% 17%" : "0 0% 100%",
        muted: isDark ? "215 27% 25%" : "210 40% 96.1%",
        border: isDark ? "215 20% 27%" : "214.3 31.8% 91.4%"
      },
      gray: {
        background: isDark ? "0 0% 9%" : "0 0% 100%",
        card: isDark ? "0 0% 14.9%" : "0 0% 100%",
        muted: isDark ? "0 0% 20%" : "0 0% 96.1%",
        border: isDark ? "0 0% 22%" : "0 0% 89.8%"
      },
      zinc: {
        background: isDark ? "240 10% 3.9%" : "0 0% 100%",
        card: isDark ? "240 4% 10%" : "0 0% 100%",
        muted: isDark ? "240 4% 16%" : "240 4.8% 95.9%",
        border: isDark ? "240 4% 18%" : "240 5.9% 90%"
      },
      neutral: {
        background: isDark ? "0 0% 10%" : "0 0% 100%",
        card: isDark ? "0 0% 15%" : "0 0% 100%",
        muted: isDark ? "0 0% 20%" : "0 0% 96.1%",
        border: isDark ? "0 0% 23%" : "0 0% 89.8%"
      },
      stone: {
        background: isDark ? "20 14.3% 4.1%" : "0 0% 100%",
        card: isDark ? "24 9.8% 10%" : "0 0% 100%",
        muted: isDark ? "12 6.5% 15.1%" : "60 4.8% 95.9%",
        border: isDark ? "12 6.5% 17%" : "20 5.9% 90%"
      }
    }

    const colors = bgMap[background] || bgMap.neutral
    root.style.setProperty("--background", colors.background)
    root.style.setProperty("--card", colors.card)
    root.style.setProperty("--muted", colors.muted)
    root.style.setProperty("--border", colors.border)
    
    // Aplicar cores na sidebar também
    root.style.setProperty("--sidebar-background", colors.card)
    root.style.setProperty("--sidebar-foreground", isDark ? "210 40% 98%" : "222.2 84% 4.9%")
    root.style.setProperty("--sidebar-accent", colors.muted)
    root.style.setProperty("--sidebar-accent-foreground", isDark ? "210 40% 98%" : "222.2 47.4% 11.2%")
    root.style.setProperty("--sidebar-border", colors.border)
  }

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
